import { useRef, useState } from "react";
import { Eye, EyeOff, Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { ADMIN_PANEL_PATH } from "@/lib/adminRoute";
import { TurnstileWidget, TURNSTILE_ENABLED } from "@/components/ui/TurnstileWidget";

export const PrivateAreaModal = ({
  open,
  onOpenChange,
  redirectPath = ADMIN_PANEL_PATH,
  title = "Área ADM",
  description = "Acesso exclusivo ADMIN",
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!username || !password) {
      setError("Preencha todos os campos");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await api.post("/login", { username, password, turnstile_token: turnstileToken || undefined });
      localStorage.setItem("admin_session", "1");
      onOpenChange(false);
      window.location.href = redirectPath;
    } catch (err) {
      turnstileRef.current?.reset();
      setTurnstileToken("");
      setError("Usuário ou senha inválidos");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setUsername("");
      setPassword("");
      setError("");
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="max-w-md border-white/5 bg-[#0A0A0A]"
        data-testid="private-area-modal"
      >
        <DialogHeader>
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-[#CC0000]/30">
              <Lock size={24} className="text-[#CC0000]" />
            </div>
          </div>
          <DialogTitle className="text-center font-['Cormorant_Garamond'] text-2xl text-white">
            {title}
          </DialogTitle>
          <DialogDescription className="text-center font-['Manrope'] text-[#A3A3A3]">
            {description}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleLogin} className="mt-4 space-y-5" data-testid="private-login-form">
          <div>
            <label className="mb-2 block text-xs font-['Manrope'] uppercase tracking-[0.15em] text-[#A3A3A3]">
              Usuário
            </label>
            <Input
              value={username}
              onChange={(event) => {
                setUsername(event.target.value);
                setError("");
              }}
              placeholder="Digite seu usuário"
              className="h-12 border-white/10 bg-transparent text-white"
            />
          </div>

          <div>
            <label className="mb-2 block text-xs font-['Manrope'] uppercase tracking-[0.15em] text-[#A3A3A3]">
              Senha
            </label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  setError("");
                }}
                placeholder="Digite sua senha"
                className="h-12 border-white/10 bg-transparent pr-12 text-white"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A3A3A3] hover:text-white"
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          {error && (
            <p className="font-['Manrope'] text-xs text-red-400" data-testid="private-error-msg">
              {error}
            </p>
          )}

          <TurnstileWidget ref={turnstileRef} onVerify={setTurnstileToken} className="flex justify-center" />

          <button
            type="submit"
            disabled={loading || (TURNSTILE_ENABLED && !turnstileToken)}
            className="btn-premium w-full py-3 text-xs uppercase tracking-[0.2em] disabled:opacity-60"
          >
            {loading ? "Entrando..." : "Acessar"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
